/**
 * Lodestar — PostHog analytics wrapper.
 *
 * Powers the habit-loop instrumentation (weekly Cosmic Forecast email
 * + signed-in /today recurring reward). PostHog is loaded globally via
 * `public/index.html`, so this is a thin defensive wrapper:
 *
 *   • Calls succeed silently when PostHog hasn't finished loading.
 *   • Calls also succeed silently in non-browser environments (SSR,
 *     unit tests, jsdom without window).
 *   • A consistent, lower-case-snake-case event taxonomy keeps the
 *     PostHog dashboard tidy.
 *
 * Event taxonomy (Phase Y — Habit Loop):
 *   • email_sent          — server-side; mirrored to PostHog if we
 *                           ever proxy email send events (currently
 *                           server-only via the `email_events` Mongo
 *                           collection).
 *   • email_opened        — fired by the tracking pixel embedded in
 *                           the weekly HTML (future).
 *   • email_clicked       — fired by the magic-link redemption page
 *                           on first paint.
 *   • today_view_opened   — fired by Today.jsx when the page mounts
 *                           (split by signed-in vs anonymous).
 *   • return_session      — fired on /today when document.referrer
 *                           contains the magic-link redemption marker
 *                           OR `localStorage.lodestar.last_magic_at`
 *                           was set within the last 5 minutes.
 *   • email_pref_changed  — fired from Account.jsx when the weekly
 *                           toggle / send-day / send-hour changes.
 */

const STORAGE_KEYS = {
    lastMagicAt: "lodestar.last_magic_at",
};

/** Internal: return window.posthog if it's ready, else null. */
function _posthog() {
    if (typeof window === "undefined") return null;
    const ph = window.posthog;
    if (!ph || typeof ph.capture !== "function") return null;
    return ph;
}

/**
 * Capture an event. Forwards to PostHog AND Google Analytics 4 — both
 * are safe to call before either script is loaded (no-ops, never throw).
 */
export function track(event, props = {}) {
    let any = false;
    // PostHog
    try {
        const ph = _posthog();
        if (ph) { ph.capture(event, props); any = true; }
    } catch (e) {
        if (typeof console !== "undefined") {
            console.debug("[analytics] posthog capture failed", event, e?.message || e);
        }
    }
    // Google Analytics 4 — only fires events when the user has granted
    // analytics_storage (Consent Mode v2 handles the gate; we just call
    // gtag directly and let it be filtered if denied).
    try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
            window.gtag("event", event, props);
            any = true;
        }
    } catch (e) {
        if (typeof console !== "undefined") {
            console.debug("[analytics] gtag event failed", event, e?.message || e);
        }
    }
    return any;
}

/** Fire a SPA pageview to GA4. PostHog auto-captures via its
 * built-in pageview snippet, but GA4 needs an explicit `page_view`
 * event on each route change since `send_page_view` only fires once
 * on initial config. */
export function trackPageview(path) {
    try {
        if (typeof window === "undefined" || typeof window.gtag !== "function") return;
        const page_path = path || (window.location.pathname + window.location.search);
        window.gtag("event", "page_view", {
            page_path,
            page_location: window.location.href,
            page_title: document.title,
        });
    } catch {
        // ignore
    }
}

/** Identify the current signed-in user (call from AuthContext after login). */
export function identify(userId, traits = {}) {
    try {
        const ph = _posthog();
        if (!ph || !userId) return false;
        ph.identify(userId, traits);
        return true;
    } catch {
        return false;
    }
}

/** Reset (call on logout). */
export function reset() {
    try {
        const ph = _posthog();
        if (!ph) return false;
        ph.reset?.();
        return true;
    } catch {
        return false;
    }
}

// ─────────────────────────────────────────────────────────────────────
// Habit-loop helpers — opinionated wrappers so call-sites stay terse
// ─────────────────────────────────────────────────────────────────────

/**
 * Stamp localStorage on a successful magic-link redemption. Today.jsx
 * checks for this stamp to fire `return_session` once per inbound click
 * (within a 5-minute freshness window).
 */
export function stampMagicReturn() {
    try {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(STORAGE_KEYS.lastMagicAt, String(Date.now()));
    } catch {
        // localStorage blocked / Safari private mode — fine, the stamp
        // is purely an analytics nicety.
    }
}

/** Has the user just arrived via a magic-link redemption (≤5 min ago)? */
export function isFreshMagicReturn(maxAgeMs = 5 * 60 * 1000) {
    try {
        if (typeof window === "undefined") return false;
        const raw = window.localStorage.getItem(STORAGE_KEYS.lastMagicAt);
        if (!raw) return false;
        const at = Number(raw);
        if (!Number.isFinite(at)) return false;
        return Date.now() - at <= maxAgeMs;
    } catch {
        return false;
    }
}

/** Convenience: fire return_session if the user just came from a magic link. */
export function maybeFireReturnSession(extraProps = {}) {
    if (!isFreshMagicReturn()) return false;
    // Clear the stamp so we only fire once per inbound click.
    try {
        if (typeof window !== "undefined") {
            window.localStorage.removeItem(STORAGE_KEYS.lastMagicAt);
        }
    } catch {
        // ignore
    }
    return track("return_session", { source: "weekly_email", ...extraProps });
}

const analytics = {
    track,
    trackPageview,
    identify,
    reset,
    stampMagicReturn,
    isFreshMagicReturn,
    maybeFireReturnSession,
};
export default analytics;
